import { router } from '@inertiajs/react'
import { KeyRound } from 'lucide-react'
import { useState } from 'react'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import { Badge } from '@/components/ui/Badge'
import { CopyableText } from '@/components/ui/CopyableText'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/Table'
import { McpConnectExamples } from '@/components/settings/McpConnectExamples'
import { useRouteParams } from '@/hooks/useRoutes'
import routes from '@/routeHelpers'
import type { McpApiKeyRow } from '@/pages/Settings.types'

interface McpApiKeysCardProps {
  keys: McpApiKeyRow[] | null
  newKeyToken: string | null
}

export function McpApiKeysCard({ keys, newKeyToken }: McpApiKeysCardProps) {
  const { withAccount } = useRouteParams()
  const [name, setName] = useState('')

  if (keys === null) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          Only owners and admins can manage API keys.
        </CardContent>
      </Card>
    )
  }

  const create = () => {
    router.post(
      routes.mcpApiKeys.create.path(withAccount()),
      { mcp_api_key: { name } },
      { preserveScroll: true, onSuccess: () => setName('') },
    )
  }

  const revoke = (key: McpApiKeyRow) => {
    router.delete(routes.mcpApiKeys.destroy.path(withAccount({ id: key.id })), { preserveScroll: true })
  }

  const formatDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString() : '—')

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><KeyRound className="size-4" /> MCP API keys</CardTitle>
        <CardDescription>
          Keys let AI agents manage your help center (and later, conversations) over MCP at{' '}
          <code className="text-xs">/mcp</code>. Each key acts as the user who created it.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {newKeyToken && (
          <div className="space-y-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3">
            <p className="text-sm font-medium">Copy your new API key now — it won't be shown again.</p>
            <CopyableText text={newKeyToken} className="font-mono text-xs break-all" />
          </div>
        )}

        <div className="flex items-end gap-2">
          <div className="flex-1 space-y-2">
            <Label htmlFor="mcp-key-name">Key name</Label>
            <Input
              id="mcp-key-name"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. helpdesk-seeder"
            />
          </div>
          <Button onClick={create} disabled={!name.trim()}>Create key</Button>
        </div>

        {keys.length > 0 && (
          <div className="rounded-md border border-border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Acts as</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Last used</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-20" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {keys.map(key => (
                  <TableRow key={key.id}>
                    <TableCell className="font-medium">{key.name}</TableCell>
                    <TableCell className="text-muted-foreground">{key.user_name}</TableCell>
                    <TableCell>{formatDate(key.created_at)}</TableCell>
                    <TableCell>{formatDate(key.last_used_at)}</TableCell>
                    <TableCell>
                      <Badge variant={key.revoked_at ? 'secondary' : 'default'}>
                        {key.revoked_at ? 'Revoked' : 'Active'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {!key.revoked_at && (
                        <ConfirmDialog
                          trigger={<Button variant="ghost" size="sm">Revoke</Button>}
                          title={`Revoke "${key.name}"?`}
                          description="Any agent using this key loses access immediately. This can't be undone."
                          confirmText="Revoke"
                          destructive
                          onConfirm={() => revoke(key)}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        <McpConnectExamples token={newKeyToken} />
      </CardContent>
    </Card>
  )
}
